import type { RfqViewState } from "./rfq-view-types";
import { createPortal } from "react-dom";
import { methodLabels } from "./rfq-presentation";

type Props = Pick<RfqViewState,
  "requestNo" |
  "title" |
  "category" |
  "quantity" |
  "unit" |
  "rfq" |
  "form" |
  "composeError" |
  "submitting" |
  "rfqCommandBusy" |
  "setForm" |
  "create" |
  "composeOpen" |
  "composeStep" |
  "composeDrawerRef" |
  "closeComposeDrawer" |
  "moveComposeStep" |
  "deadlinePreview" |
  "reviewDeadline" |
  "neededAtLabel"
>;
export function RfqComposeDialog({
  requestNo,
  title,
  category,
  quantity,
  unit,
  rfq,
  form,
  composeError,
  submitting,
  rfqCommandBusy,
  setForm,
  create,
  composeOpen,
  composeStep,
  composeDrawerRef,
  closeComposeDrawer,
  moveComposeStep,
  deadlinePreview,
  reviewDeadline,
  neededAtLabel,
}: Props) {
  return (
    composeOpen && !rfq
      ? createPortal(
          <div className="rfq-invitation-drawer-layer rfq-create-drawer-layer">
            <div
              className="rfq-invitation-drawer-backdrop"
              aria-hidden="true"
              onClick={closeComposeDrawer}
            />
            <aside
              ref={composeDrawerRef}
              className="rfq-invitation-drawer rfq-create-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rfq-create-drawer-title"
            >
              <header>
                <div>
                  <span className="section-kicker">
                    Yeni satın alma adımı
                  </span>
                  <h2
                    id="rfq-create-drawer-title"
                    tabIndex={-1}
                    data-compose-initial
                  >
                    Teklif Talebi oluştur
                  </h2>
                  <p>
                    Teklif yöntemini ve son katılım zamanını belirleyin.
                  </p>
                  <dl
                    className="rfq-create-request-summary"
                    aria-label="Talep özeti"
                  >
                    <div>
                      <dt>Talep</dt>
                      <dd>{requestNo}</dd>
                    </div>
                    <div>
                      <dt>Kategori</dt>
                      <dd>{category}</dd>
                    </div>
                    <div>
                      <dt>Miktar</dt>
                      <dd>
                        {quantity} {unit}
                      </dd>
                    </div>
                    <div>
                      <dt>İhtiyaç tarihi</dt>
                      <dd>{neededAtLabel}</dd>
                    </div>
                  </dl>
                </div>
                <button
                  className="icon-button"
                  aria-label="Teklif Talebi oluşturmayı kapat"
                  onClick={closeComposeDrawer}
                >
                  ×
                </button>
              </header>
              <div className="rfq-invitation-drawer-body rfq-create-drawer-body">
                <ol
                  className="rfq-create-progress"
                  aria-label="Oluşturma adımları"
                >
                  {["Zamanlama", "Gözden geçir"].map(
                    (label, index) => (
                      <li
                        key={label}
                        className={
                          composeStep === index + 1
                            ? "active"
                            : composeStep > index + 1
                              ? "complete"
                              : ""
                        }
                      >
                        <span>{index + 1}</span>
                        <strong>{label}</strong>
                      </li>
                    ),
                  )}
                </ol>
                {composeError ? (
                  <div className="error-state" role="alert">
                    {composeError}
                  </div>
                ) : null}
                {composeStep === 1 ? (
                  <section
                    className="rfq-create-group"
                    aria-labelledby="rfq-create-settings"
                  >
                    <div>
                      <h3 id="rfq-create-settings">Zamanlama ve yöntem</h3>
                      <p>
                        Teklif toplama yöntemini ve son katılım zamanını
                        belirleyin.
                      </p>
                    </div>
                    <label>
                      Teklif yöntemi
                      <select
                        data-step-initial
                        value={form.method}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            method: event.target.value,
                          }))
                        }
                      >
                        <option value="OPEN">Açık teklif</option>
                        <option value="SEALED">Kapalı teklif</option>
                        <option value="SOLE_SOURCE">Tek kaynak</option>
                        <option value="EMERGENCY">Acil alım</option>
                      </select>
                    </label>
                    <label>
                      Yerel teklif son tarihi
                      <input
                        type="datetime-local"
                        value={form.deadlineAt}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            deadlineAt: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Saat dilimi
                      <input
                        value={form.timezone}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            timezone: event.target.value,
                          }))
                        }
                      />
                      <small>Örnek: Europe/Istanbul</small>
                    </label>
                    <p className="rfq-deadline-preview" role="status">
                      {deadlinePreview}
                    </p>
                    {["SOLE_SOURCE", "EMERGENCY"].includes(form.method) ? (
                      <label>
                        Yöntem gerekçesi
                        <textarea
                          value={form.reason}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              reason: event.target.value,
                            }))
                          }
                        />
                      </label>
                    ) : null}
                  </section>
                ) : null}
                {composeStep === 2 ? (
                  <section
                    className="rfq-create-review"
                    aria-labelledby="rfq-create-review-heading"
                  >
                    <div>
                      <h3
                        id="rfq-create-review-heading"
                        tabIndex={-1}
                        data-step-initial
                      >
                        Gözden geçir
                      </h3>
                      <p>
                        Planlamadan gelen ürün ve miktarı kontrol edin.
                        Taslak oluşturulduktan sonra fiyat istenecek
                        tedarikçileri seçeceksiniz.
                      </p>
                    </div>
                    <dl>
                      <div>
                        <dt>Teklif yöntemi</dt>
                        <dd>{methodLabels[form.method] ?? form.method}</dd>
                      </div>
                      <div>
                        <dt>Son tarih</dt>
                        <dd>{reviewDeadline}</dd>
                      </div>
                      <div>
                        <dt>Ürün</dt>
                        <dd>{title}</dd>
                      </div>
                      <div>
                        <dt>Miktar</dt>
                        <dd>
                          {quantity} {unit}
                        </dd>
                      </div>
                      <div>
                        <dt>İhtiyaç tarihi</dt>
                        <dd>{neededAtLabel}</dd>
                      </div>
                    </dl>
                  </section>
                ) : null}
              </div>
              <footer>
                <button
                  className="secondary-button"
                  disabled={rfqCommandBusy}
                  onClick={() =>
                    composeStep === 1
                      ? closeComposeDrawer()
                      : moveComposeStep(1)
                  }
                >
                  {composeStep === 1 ? "Vazgeç" : "Geri"}
                </button>
                <button
                  className="approve-button"
                  disabled={rfqCommandBusy}
                  onClick={() =>
                    composeStep === 2
                      ? create()
                      : moveComposeStep(2)
                  }
                >
                  {submitting
                    ? "Oluşturuluyor…"
                    : composeStep === 2
                      ? "Taslağı oluştur"
                      : "Devam"}
                </button>
              </footer>
            </aside>
          </div>,
          document.body,
        )
      : null
  );
}
